import * as animateScrollTo from 'animated-scroll-to';
import * as React from 'react';

import { ImmutableInterface } from '../../../types';
import BlockAlpha from '../LearnBlocks/BlockAlpha';
import { BlockEquation } from '../LearnBlocks/BlockEquation';
import { BlockHeading } from '../LearnBlocks/BlockHeading';
import { BlockImage } from '../LearnBlocks/BlockImage';
import { BlockSvg } from '../LearnBlocks/BlockSvg';
import { BlockTable } from '../LearnBlocks/BlockTable';
import { BlockText } from '../LearnBlocks/BlockText';

import { Heading, TableOfContents } from './TableOfContents';
import Immutable from 'immutable';

const MAXIMUM_WAIT_FOR_NEXT_LAYOUT_SHIFT_MS = 500;

interface LearnContentProps {
  item: Immutable.List<any>;
  headerIndex?: Immutable.List<Immutable.Record<Heading>>;
  scrollElement?: string;
  // This function will handle the clone alpha when the user is on the simulate page
  handleSimulateClone?: (alpha: string) => void;
  handleShowModal?: () => boolean;
  isMultiTab?: boolean;
}

const contentBlock = new Set(['HEADING', 'TEXT', 'IMAGE', 'TABLE', 'SIMULATION_EXAMPLE', 'SVG', 'EQUATION']);

export class LearnContent extends React.Component<LearnContentProps> {
  isScrolled: boolean;
  containerObserver: ResizeObserver;
  containerRef: React.RefObject<HTMLDivElement | null>;

  constructor(props: LearnContentProps) {
    super(props);

    this.isScrolled = false;
    this.containerRef = React.createRef<HTMLDivElement>();
    this.containerObserver = new ResizeObserver(() => this.scheduleScrollIfNeeded());
  }

  componentDidMount() {
    if (window.location.hash !== '') {
      this.containerObserver.observe(this.containerRef.current!);
      this.scrollIfNeeded();
    } else {
      // early disconnect resize observer
      this.containerObserver.disconnect();
    }
  }

  componentWillUnmount() {
    this.containerObserver.disconnect();
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = null;
    }
  }

  // Schedule the one-time hash-scroll. We use a single leading timeout rather than
  // `debounce`, because `debounce` resets its timer on every call — and the ResizeObserver
  // fires on every layout shift (monaco editor layout, table reflow, image load). Under
  // React 19 those shifts keep the debounce perpetually resetting, so it never fires and
  // the observer never disconnects, pinning the CPU (and crashing the page when many
  // operators are expanded at once). Cap the wait so it always settles within the budget.
  scrollTimer: ReturnType<typeof setTimeout> | null = null;
  scheduleScrollIfNeeded = () => {
    if (this.isScrolled || this.scrollTimer) return;
    this.scrollTimer = setTimeout(() => {
      this.scrollTimer = null;
      this.scrollIfNeeded();
    }, MAXIMUM_WAIT_FOR_NEXT_LAYOUT_SHIFT_MS);
  };
  scrollIfNeeded = () => {
    if (this.isScrolled) return;
    this.isScrolled = true;
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = null;
    }
    this.containerObserver.disconnect();
    if (window.location.hash !== '') {
      this.scrollToElementId(window.location.hash.substring(1));
    }
  };

  getContentComponent(contentType: string, content: any) {
    const components: any = {
      HEADING: { Component: BlockHeading, props: this.headingProps(content) },
      TEXT: { Component: BlockText, props: this.textProps(content) },
      IMAGE: { Component: BlockImage, props: this.imageProps(content) },
      TABLE: { Component: BlockTable, props: this.tableProps(content) },
      SIMULATION_EXAMPLE: { Component: BlockAlpha, props: this.simulationProps(content) },
      SVG: { Component: BlockSvg, props: this.svgProps(content) },
      EQUATION: { Component: BlockEquation, props: this.equationProps(content) },
    };

    return components[contentType];
  }

  headingProps = (content: any) => ({
    value: content.getIn(['value', 'content']) ? content.getIn(['value', 'content']) : content.get('value'),
    level: content.get('level') ? content.get('level') : content.getIn(['value', 'level']),
  });

  textProps = (content: any) => ({
    value: content.get('value'),
  });

  simulationProps = (content: any) => ({
    alpha: content.get('value'),
    handleSimulateClone: this.props.handleSimulateClone,
    handleShowModal: this.props.handleShowModal,
    isMultiTab: this.props.isMultiTab,
    learnBlockId: content.get('id'),
  });

  tableProps = (content: any) => ({
    data: content.getIn(['value', 'data']),
    firstRowIsTableHeader: content.getIn(['value', 'firstRowIsTableHeader']),
    firstColIsHeader: content.getIn(['value', 'firstColIsHeader']),
  });

  imageProps = (content: any) => ({
    title: content.getIn(['value', 'title']),
    url: content.getIn(['value', 'url']),
    width: content.getIn(['value', 'width']),
    height: content.getIn(['value', 'height']),
  });

  svgProps = (content: any) => ({
    url: content.get('value'),
  });

  equationProps = (content: any) => ({
    equation: content.get('value'),
  });

  scrollToElementId(id: string) {
    const elementToScroll = this.props.scrollElement ? document.querySelector(this.props.scrollElement) : undefined;
    const elementToScrollTo = document.getElementById(id);
    animateScrollTo(elementToScrollTo, { element: elementToScroll });
  }

  scrollToAnchor = (id: string) => {
    window.history.pushState({}, '', `#${id}`);
    this.scrollToElementId(id);
  };

  makeLinkClickHandler =
    (id: string): ((event: React.MouseEvent<HTMLAnchorElement>) => void) =>
    event => {
      if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
        return;
      }

      // Only handle click event without any key holding
      // It will let users open the link in new tab
      event.stopPropagation();
      this.scrollToAnchor(id);
    };

  render() {
    const blocks = (this.props.item ?? []).filter((block: ImmutableInterface<any>) => contentBlock.has(block.get('type')));

    return (
      <div ref={this.containerRef}>
        {this.props.headerIndex && <TableOfContents headings={this.props.headerIndex} makeLinkClickHandler={this.makeLinkClickHandler} />}
        {blocks.map((block: ImmutableInterface<any>) => {
          const ContentBlock = this.getContentComponent(block.get('type'), block);
          return (
            <div className="learn__block" key={`content-block-${block.get('id')}`}>
              <ContentBlock.Component {...ContentBlock.props} />
            </div>
          );
        })}
      </div>
    );
  }
}
